import type { SheetProps } from '@hover/blueprint';
import { RadioGroup, Sheet, system } from '@hover/blueprint';
import React from 'react';

import { useIsEmbed } from 'src/shared/hooks';
import { useScrollTo } from 'src/shared/hooks/scrollHooks/useScrollTo';
import { hideScrollbars, hideScrollbarsStyles } from 'src/shared/utils/styles';

import { Footer } from './Footer';
import { Header } from './Header';

const HiddenScroll = hideScrollbars(system.div);

export interface SelectPaletteSheetProps extends SheetProps {
  selectedPalette: string | undefined;
  onSelect: (palette: string) => void;
  isLoading?: boolean;
}

export interface SelectPaletteSheetLoadingProps {
  placeholderCount?: number;
}

export const SelectPaletteSheet = ({
  children,
  header,
  onSelect,
  selectedPalette,
  isLoading,
  ...props
}: SelectPaletteSheetProps) => {
  const [scroll, { ref: scrollRef, atLeft, atRight }] = useScrollTo();
  const { onClose } = props;

  const paddingX = { base: 500, tablet: 900 };
  const isEmbed = useIsEmbed();

  return (
    <Sheet
      autoFocus={isEmbed ? false : undefined}
      blockScrollOnMount={isEmbed ? false : undefined}
      contentProps={{
        pt: { base: 800, tablet: 1000 },
        pl: 0,
        pr: 0,
        sx: {
          header: {
            mb: { base: 600, tablet: 700 },
          },
          footer: {
            mb: { base: 0, tablet: 800 },
            mt: 500,
            px: paddingX,
          },
        },
      }}
      footer={
        <Footer
          atLeft={atLeft}
          atRight={atRight}
          onClose={onClose}
          scroll={scroll}
        />
      }
      header={<Header header={header} paddingX={paddingX} />}
      variant="bottom"
      {...props}
    >
      <HiddenScroll overflowX="scroll" ref={scrollRef}>
        <RadioGroup
          display="flex"
          flexDirection="row"
          gap="400"
          onChange={isLoading ? undefined : onSelect}
          pb="200"
          px={paddingX}
          sx={hideScrollbarsStyles()}
          value={selectedPalette}
          width="fit-content"
        >
          {children}
        </RadioGroup>
      </HiddenScroll>
    </Sheet>
  );
};
